import { readAgentPageContent } from './page-content';

export interface AgentConversationSnapshot {
  conversationId: string;
  platform: string;
  shopId: string;
  region?: string;
  buyer: { nickname: string };
  stream: 'buyer' | 'mercado';
  scope: 'loadedMessages';
  truncated: boolean;
  messages: {
    msgId: string;
    from: 'buyer' | 'bot' | 'assistant' | 'system';
    at: string;
    kind: string;
    text: string;
    status: string;
  }[];
}

export interface AgentPageContext {
  client_app: 'fett';
  workspace?: string;
  path: string;
  module: null;
  readable: boolean;
  capturedAt: string;
  conversation?: AgentConversationSnapshot;
  sections?: {
    name: 'page';
    status: 'ready';
    truncated: boolean;
    items: { text: string }[];
  }[];
}

export type AgentPageReader = () => Pick<AgentPageContext, 'conversation'> | undefined;

// 业务页面显式提供只读快照；发送时采集，不保存页面内容或主动加载历史数据。
export function createAgentPageContextRegistry(
  log: (event: string, fields: Record<string, string>) => void,
) {
  const readers = new Set<{ path: string; read: AgentPageReader }>();
  return {
    register(path: string, read: AgentPageReader) {
      const entry = { path, read };
      readers.add(entry);
      return () => {
        readers.delete(entry);
      };
    },
    read(path: string, workspace?: string): AgentPageContext {
      const context: AgentPageContext = {
        client_app: 'fett',
        workspace,
        path,
        module: null,
        readable: false,
        capturedAt: new Date().toISOString(),
      };
      for (const entry of readers) {
        if (entry.path !== path) continue;
        try {
          const snapshot = entry.read();
          if (snapshot?.conversation) {
            context.conversation = snapshot.conversation;
            context.readable = true;
          }
        } catch {
          log('agent.page_context.failed', { workspace: workspace || '' });
        }
      }
      const visible = readAgentPageContent(workspace === 'agent' ? 'form' : 'workspace');
      if (visible.text) {
        context.sections = [{
          name: 'page',
          status: 'ready',
          truncated: visible.truncated,
          items: [{ text: visible.text }],
        }];
        context.readable = true;
      }
      log('agent.page_context.captured', {
        workspace: workspace || '',
        readable: String(context.readable),
        messageCount: String(context.conversation?.messages.length || 0),
        pageChars: String(visible.text.length),
        truncated: String(context.conversation?.truncated || false),
      });
      return context;
    },
  };
}
